import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleDismissLink, true);

    const storedState = window.sessionStorage.getItem('cartDrawerOpen');
    const hasOpenAttr = this.refs.dialog.hasAttribute('open');

    if (storedState === 'true' || hasOpenAttr) {
      // Ensure sessionStorage is synced
      if (storedState !== 'true') {
        window.sessionStorage.setItem('cartDrawerOpen', 'true');
      }

      // If it has the open attribute, upgrade to modal WITHOUT closing first
      if (hasOpenAttr) {
        try {
          this.refs.dialog.showModal();
        } catch (e) {
          // Expected: dialog has 'open' attribute, can't call showModal
          this.refs.dialog.removeAttribute('open');
          this.refs.dialog.showModal();
        }
        this.dispatchEvent(new DialogOpenEvent());
      } else if (storedState === 'true') {
        // If only sessionStorage is true, use normal flow
        this.showDialog();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleDismissLink, true);
  }

  #handleDialogOpen = () => {
    window.sessionStorage.setItem('cartDrawerOpen', 'true');
    this.#loadRecommendationsScript();
  };

  #handleDialogClose = () => {
    window.sessionStorage.setItem('cartDrawerOpen', 'false');
  };

  /**
   * A link that leaves the cart (Continue shopping) must clear the open flag
   * before navigation. Otherwise the next page sees cartDrawerOpen and
   * reopens the drawer in connectedCallback.
   *
   * @param {MouseEvent} event
   */
  #handleDismissLink = (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target?.closest('[data-cart-drawer-dismiss]')) return;
    window.sessionStorage.setItem('cartDrawerOpen', 'false');
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    window.sessionStorage.setItem('cartDrawerOpen', 'true');
    this.#loadRecommendationsScript();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  showDialog() {
    window.sessionStorage.setItem('cartDrawerOpen', 'true');
    super.showDialog();
  }

  close() {
    window.sessionStorage.setItem('cartDrawerOpen', 'false');
    this.closeDialog();
  }

  #loadRecommendationsScript() {
    if (document.querySelector('script[data-cart-recommendations]')) return;

    const assetUrl = document.querySelector('script[src*="cart-drawer.js"]')?.src;
    if (!assetUrl) return;

    const script = document.createElement('script');
    script.type = 'module';
    script.dataset.cartRecommendations = 'true';
    script.src = assetUrl.replace('cart-drawer.js', 'product-recommendations.js');
    document.head.appendChild(script);
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
